import * as modifications from './alan_api';
import * as widget from '../../libraries/gui_definition_lib/types/widget/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};

type Mutable<T> = { -readonly[P in keyof T]: T[P] };
export type Subscription = {
	unsubscribe:() => void;
}

export type DestructionObserver = () => void;
export interface IDestructable {
	readonly destroyed: boolean;
	destroy(): void;
	onDestroy(observer: DestructionObserver): Subscription;
}
export interface ValueObserver {
	on_update: (value:string|number|undefined, subscription: Subscription) => void;
}
export interface TextObserver extends ValueObserver {
	on_update: (value:string, subscription: Subscription) => void;
}
export interface NumberObserver extends ValueObserver {
	on_update: (value:number, subscription: Subscription) => void;
}
export interface StateObserver extends ValueObserver {
	on_update: (value:undefined, subscription: Subscription) => void;
}
export interface DictionaryObserver<T> {
	on_add?: (entry:T) => void,
	on_remove?: (key:string) => void
}
export interface SetObserver<T> {
	on_add?: (entry:T) => void,
	on_remove?: () => void
}
type RefCountable = {
	__ref_count: number;
	__ref_set: Set<any>;
};
function isRefCountable(obj:any): RefCountable|undefined {
	if(obj instanceof AlanNode &&
		obj.entity instanceof AlanDictionaryEntry &&
		obj.entity["__ref_count"] !== undefined) {
		return obj.entity
	}
	return undefined;}

function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	return (detach = false) => {
		return callback(detach);
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	return (detach = false) => {
		return callback(detach);
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject implements IDestructable {
	public abstract get path(): string;
	private destruction_observers: Set<() => void> | undefined = undefined;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
	public destroy(): void {
		this._destroyed = true;
		if (this.destruction_observers) {
			for (const observer of this.destruction_observers) {
				(<any>this).root?.defer_destruction(() => observer());
			}
			this.destruction_observers.clear();
		}
	}
	onDestroy(observer: DestructionObserver): Subscription {
		if (this.destroyed) {
			observer();
		} else {
			if (!this.destruction_observers) {
				this.destruction_observers = new Set();
			}
			this.destruction_observers.add(observer);
		}
		return {
			unsubscribe: () => this.destruction_observers?.delete(observer),
		};
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	private referenced_entry:T|undefined;
	private counted_entry:RefCountable|undefined;
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { 
		super();
	}
	public get ref() {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.referenced_entry !== undefined) { return this.referenced_entry; }
		this.referenced_entry = this.resolve();
		this.counted_entry = isRefCountable(this.referenced_entry)
		if (this.counted_entry !== undefined) {
			this.counted_entry.__ref_count += 1
			if (debug) {
				if (this.counted_entry.__ref_set === undefined) { this.counted_entry.__ref_set = new Set<any>(); }
				this.counted_entry.__ref_set.add(this);
			}
		}
		return this.referenced_entry
	}
	public unlink(): void {
		if (this.counted_entry !== undefined) {
			this.counted_entry.__ref_count -= 1;
			this.counted_entry.__ref_set?.delete(this);
			this.counted_entry = undefined;
		}
		this.referenced_entry = undefined;
	}
	public destroy(): void {
		super.destroy();
		this.unlink();
	}

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries: Map<string, T['node']>;
	protected observers:Set<DictionaryObserver<T['node']>> = new Set();

	constructor (entries:{[key:string]:T['init']},	protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, this.initialize(parent, entry_key, entries[entry_key])]));
	}

	protected abstract initialize(parent:P, key:string, obj:T['init']): T['node'];
	public destroy(): void {
		super.destroy();
		for (const entry of this._entries.values()) {
			entry.destroy();
		}
	}

	get size() { return this._entries.size; }
	[Symbol.iterator]():IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	entries(): IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	values(): IterableIterator<T['node']> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.values();
	}
	keys(): IterableIterator<string> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return Array.from(this.entries());
	}
	map<RT>(callback:(value:T['node']) => RT): Record<string, RT> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node']|undefined {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.get(key);
	}
	has(key:string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key:string, onExists:(($: T['node']) => RT)|Exclude<RT, Function>, onNotExists:(() => RT)|Exclude<RT, Function>): RT {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry) : onExists;
		}
	}
	create(key:string, init:T['init']): T['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this._entries.has(key)) return this._entries.get(key)!;
		const entry = this.initialize(this.parent, key, init);
		this._entries.set(key, entry);
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_add?.(entry)), this);
		return entry;
	}

	delete(key:string): boolean {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) return false;
		this._entries.delete(key);
		this.parent.root.transact(() => entry.destroy());
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_remove?.(key)), this);
		return true;
	}
	subscribe(observer:DictionaryObserver<T['node']>): Subscription {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		this.observers.add(observer);
		const subscription:Subscription = {
			unsubscribe: () => this.observers.delete(observer)
		};
		return subscription;
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	private observers:Set<SetObserver<T['node']>> = new Set();
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	public destroy():void {
		super.destroy();
		this._entries.forEach(entry => entry.destroy());
	}

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
	create(init:T['init']): T['node'] {
		const entry = this.initialize(this.parent, init);
		this._entries.add(entry);
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_add?.(entry)), this);
		return entry;
	}
	delete(entry:T['node']):boolean {
		if (!this._entries.has(entry)) return false;
		this._entries.delete(entry);
		this.parent.root.transact(() => entry.destroy());
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_remove?.()), this);
		return true;
	}
	subscribe(observer:SetObserver<T['node']>):Subscription {
		const $this = this;
		$this.observers.add(observer);
		const subscription:Subscription = {
			unsubscribe: () => $this.observers.delete(observer)
		};
		return subscription;
	}
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cmodifications
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}

	set<S extends T['name']>(state_name:S, init:Extract<T, {name:S}>['init']): Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const parent = this.state.node.parent;
		const current_state = this.state;
		this.state = {
			name: state_name as T['name'],
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
		this.root.transact(() => current_state.node.destroy());
		(this.parent as any).notify(this.name);
		return this.state.node;
	}
	ensure<S extends T['name']>(state_name:S, init:Extract<T, {name:S}>['init']): Extract<T, {name:S}>['node'] {
		if (this.state.name !== state_name) {
			this.set(state_name, init);
		}
		return this.state.node;
	}
	public subscribe(observer: StateObserver): Subscription {
		const subscription = (this.parent as any)._subscribe_property(this.name, observer) as Subscription;
		observer.on_update(undefined, subscription);
		return subscription;
	}
	public destroy(): void {
		super.destroy();
		this.state.node.destroy();
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cmodifications|undefined;
	public abstract get root():Cmodifications;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
	private property_observers: Map<string, Set<[ValueObserver, Subscription]>> | undefined = undefined;
	protected _subscribe_property(key:string, obs:ValueObserver):Subscription {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.property_observers === undefined) {
			this.property_observers = new Map()
		}
		if (!this.property_observers.has(key)) {
			this.property_observers.set(key, new Set());
		}

		const subscription = {
			unsubscribe: () => this.property_observers?.get(key)?.delete(property_observer)
		};
		const property_observer:[ValueObserver, Subscription] = [obs, subscription];
		this.property_observers.get(key)!.add(property_observer);
		return subscription;
	}
	protected notify(key:string, value?:(string|number)) {
		const prop = this.property_observers?.get(key);
		if (prop !== undefined) {
			for (const [observer, subscription] of prop) {
				this.root.defer(() => observer.on_update?.(value, subscription), this);
			}
		}
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
	public __ref_count:number = 0;
	public __ref_set:Set<any>;public abstract parent:AlanNode;
	public destroy(): void {
		super.destroy();
		this.parent.root.defer(() => {
			if (this.__ref_count > 0) {
				const refs = (debug) ? ' ' + [...this.__ref_set].map(ref => ref.path).join('\n') : ''
				throw new Error(`Entry is still referenced! refcount: ${this.__ref_count} ${this.path}${refs}`);
			}
		})
	}
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Twidget_configuration_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes];
};
export class Cwidget_configuration_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cwidget_configuration_path.Dhas_steps
	};
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result()).result!)
	};
	constructor(init:Twidget_configuration_path, public location:AlanNode, public input: {
		node: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cwidget_configuration_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_steps.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno = {
};
export class Cno extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno, public parent:Cwidget_configuration_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes = {
	'attribute':string;
	'tail':Twidget_configuration_path;
	'type':'binding'|['binding', {}]|['configuration', Tconfiguration]|'window'|['window', {}];
};
export class Cyes extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes.Dattribute,
		readonly tail:Cwidget_configuration_path,
		readonly type:Cyes.Dtype
	};
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result()).result!)
	}
	constructor(init:Tyes, public parent:Cwidget_configuration_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes.Dattribute(init['attribute'], $this),
			tail: new Cyes.Dtail(init['tail'], $this),
			type: new Cyes.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.attribute.destroy();
		this.properties.tail.destroy();
		this.properties.type.destroy();
	}
	public set_attribute(value:string):void {
		this.properties.attribute.unlink();
		(<(detach?:boolean) => void>(this.properties.attribute as any).resolve)(true);
		(this.properties.attribute as Mutable<Cyes.Dattribute>).entry = value;
		super.notify('attribute', value);
	}
	public subscribe_attribute(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('attribute', observer);
		observer.on_update(this.properties.attribute.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbinding = {
};
export class Cbinding extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.binding_type())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		binding_type: () => widget.Cbinding__type__attributes
	} = {
		binding_type: cache((detach:boolean) => {
			const modifications__widget_configuration_path__has_steps__yes__type__binding_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'binding') {
						return resolve(context.properties.type.state.node as widget.Cbinding__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tbinding, public parent:Cyes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'binding'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration = {
	'type':'list'|['list', {}]|['state', Tstate];
};
export class Cconfiguration extends AlanNode {
	public readonly properties:{
		readonly type:Cconfiguration.Dtype
	};
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.type?.state.node.output.result()).result!)
	}
	public readonly inferences:{
		configuration_type: () => widget.Cconfiguration__type__attributes__widget_configuration_node
	} = {
		configuration_type: cache((detach:boolean) => {
			const modifications__widget_configuration_path__has_steps__yes__type__configuration_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tconfiguration, public parent:Cyes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cconfiguration.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'configuration'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlist = {
};
export class Clist extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.list_type())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		list_type: () => widget.Clist__type
	} = {
		list_type: cache((detach:boolean) => {
			const modifications__widget_configuration_path__has_steps__yes__type__configuration__type__list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration_type())
				.then(context => {
					if (context?.properties.type.state.name === 'list') {
						return resolve(context.properties.type.state.node as widget.Clist__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tlist, public parent:Cconfiguration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate = {
	'state':string;
};
export class Cstate extends AlanNode {
	public readonly properties:{
		readonly state:Cstate.Dstate
	};
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		state_group_type: () => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node
	} = {
		state_group_type: cache((detach:boolean) => {
			const modifications__widget_configuration_path__has_steps__yes__type__configuration__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.configuration_type())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate, public parent:Cconfiguration) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate.Dstate(init['state'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.state.destroy();
	}
	public set_state(value:string):void {
		this.properties.state.unlink();
		(<(detach?:boolean) => void>(this.properties.state as any).resolve)(true);
		(this.properties.state as Mutable<Cstate.Dstate>).entry = value;
		super.notify('state', value);
	}
	public subscribe_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state', observer);
		observer.on_update(this.properties.state.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Twindow = {
};
export class Cwindow extends AlanNode {
	public readonly output:{
		result: () => widget.Cwidget_configuration_node;
	} = {
		result: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.window_type())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		window_type: () => widget.Cwindow__type
	} = {
		window_type: cache((detach:boolean) => {
			const modifications__widget_configuration_path__has_steps__yes__type__window_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'window') {
						return resolve(context.properties.type.state.node as widget.Cwindow__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twindow, public parent:Cyes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'window'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconfiguration_node = {
	'attributes':Record<string, Tattributes>;
};
export class Cconfiguration_node extends AlanNode {
	public readonly properties:{
		readonly attributes:Cconfiguration_node.Dattributes
	};
	constructor(init:Tconfiguration_node, public location:AlanNode, public input: {
		widget_node: () => widget.Cwidget_configuration_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cconfiguration_node.Dattributes(init['attributes'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.attributes.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes extends Reference<widget.Cattributes__widget_configuration_node, string> {
	constructor(key:string, $this:Cattributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.widget_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes = {
	'type':['number', Tnumber]|['state group', Tstate_group]|['text', Ttext];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:Kattributes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cconfiguration_node) {
		super();
		const $this = this;
		this.key = new Kattributes(key, $this);
		this.properties = {
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnumber = {
	'value':number;
};
export class Cnumber extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber.Dvalue
	};
	public readonly inferences:{
		number_type: () => widget.Cnumber__type__configuration__type__attributes__widget_configuration_node
	} = {
		number_type: cache((detach:boolean) => {
			const modifications__configuration_node__attributes__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return context.properties.type.state.node as widget.Cnumber__type__configuration__type__attributes__widget_configuration_node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber.Dvalue(init['value'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.value.destroy();
	}
	public set_value(data:number):void {
		(this.properties.value as Mutable<Cnumber.Dvalue>).value = data;
		super.notify('value', data);
	}
	public subscribe_value(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group = {
	'node':Tconfiguration_node;
	'selected state':string;
};
export class Cstate_group extends AlanNode {
	public readonly properties:{
		readonly node:Cconfiguration_node,
		readonly selected_state:Cstate_group.Dselected_state
	};
	public readonly inferences:{
		state_group_type: () => widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node
	} = {
		state_group_type: cache((detach:boolean) => {
			const modifications__configuration_node__attributes__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node)
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			node: new Cstate_group.Dnode(init['node'], $this),
			selected_state: new Cstate_group.Dselected_state(init['selected state'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.node.destroy();
		this.properties.selected_state.destroy();
	}
	public set_selected_state(value:string):void {
		this.properties.selected_state.unlink();
		(<(detach?:boolean) => void>(this.properties.selected_state as any).resolve)(true);
		(this.properties.selected_state as Mutable<Cstate_group.Dselected_state>).entry = value;
		super.notify('selected state', value);
	}
	public subscribe_selected_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('selected state', observer);
		observer.on_update(this.properties.selected_state.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext = {
	'value':string;
};
export class Ctext extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly inferences:{
		text_type: () => widget.Ctext__type__configuration__type__attributes__widget_configuration_node
	} = {
		text_type: cache((detach:boolean) => {
			const modifications__configuration_node__attributes__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'configuration') {
						return resolve(context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return context.properties.type.state.node as widget.Ctext__type__configuration__type__attributes__widget_configuration_node;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Ctext['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}

export type Tmodifications = {
	'configuration':Tconfiguration_node;
	'path':Twidget_configuration_path;
	'selected state':string;
	'state group':string;
	'widget':string;
};
export class Cmodifications extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly configuration:Cconfiguration_node,
		readonly path:Cwidget_configuration_path,
		readonly selected_state:Cmodifications.Dselected_state,
		readonly state_group:Cmodifications.Dstate_group,
		readonly widget:Cmodifications.Dwidget
	};
	constructor(init:Tmodifications, public readonly input: {
	'widget':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			configuration: new Cmodifications.Dconfiguration(init['configuration'], $this),
			path: new Cmodifications.Dpath(init['path'], $this),
			selected_state: new Cmodifications.Dselected_state(init['selected state'], $this),
			state_group: new Cmodifications.Dstate_group(init['state group'], $this),
			widget: new Cmodifications.Dwidget(init['widget'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.configuration.destroy();
		this.properties.path.destroy();
		this.properties.selected_state.destroy();
		this.properties.state_group.destroy();
		this.properties.widget.destroy();
	}
	public set_selected_state(value:string):void {
		this.properties.selected_state.unlink();
		(<(detach?:boolean) => void>(this.properties.selected_state as any).resolve)(true);
		(this.properties.selected_state as Mutable<Cmodifications.Dselected_state>).entry = value;
		super.notify('selected state', value);
	}
	public subscribe_selected_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('selected state', observer);
		observer.on_update(this.properties.selected_state.entry, subscription);
		return subscription;
	}
	public set_state_group(value:string):void {
		this.properties.state_group.unlink();
		(<(detach?:boolean) => void>(this.properties.state_group as any).resolve)(true);
		(this.properties.state_group as Mutable<Cmodifications.Dstate_group>).entry = value;
		super.notify('state group', value);
	}
	public subscribe_state_group(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state group', observer);
		observer.on_update(this.properties.state_group.entry, subscription);
		return subscription;
	}
	public set_widget(value:string):void {
		this.properties.widget.unlink();
		(<(detach?:boolean) => void>(this.properties.widget as any).resolve)(true);
		(this.properties.widget as Mutable<Cmodifications.Dwidget>).entry = value;
		super.notify('widget', value);
	}
	public subscribe_widget(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('widget', observer);
		observer.on_update(this.properties.widget.entry, subscription);
		return subscription;
	}
	public get path() { return ``; }

	private _transacting: boolean = false;
	public get transacting() {
		return this._transacting
	}
	private _deferred_subscribers = new Set<() => void>();
	defer(callback: () => void, destructor?: IDestructable): void {
		this._deferred_subscribers.add(() => destructor?.destroyed ? void 0 : callback());
		this.notify_subscribers();
	}
	private _deferred_destruction_subscribers = new Set<() => void>();
	defer_destruction(callback: () => void): void {
		this._deferred_destruction_subscribers.add(() => callback());
		this.notify_subscribers();
	}
	private _notifying_subscribers = false;
	private notify_subscribers() {
		if (this._transacting === false && this._notifying_subscribers === false) {
			this._notifying_subscribers = true;
			let subscriber: () => void
			while (subscriber = this._deferred_destruction_subscribers.values().next().value ?? this._deferred_subscribers.values().next().value) {
				this._deferred_destruction_subscribers.delete(subscriber);
				this._deferred_subscribers.delete(subscriber);
				subscriber();
			}
			this._deferred_subscribers.clear();
			this._notifying_subscribers = false;
		}
	}
	public transact(doapply:() => void) {
		if (this._transacting === true) {
			doapply();
		} else {
			this._transacting = true;
			doapply();
			this._transacting = false;
			this.notify_subscribers();
		}
	}
	public get entity() { return this; }
}

/* property classes */
export namespace Cconfiguration_node {
	export class Dattributes extends AlanDictionary<{ node:Cattributes, init:Tattributes},Cconfiguration_node> {
		protected initialize(parent:Cconfiguration_node, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tconfiguration_node['attributes'], parent:Cconfiguration_node) {
			super(data, parent);
		}
	}
}
export namespace Cattributes {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber, init:Tnumber}|
		{ name: 'state group', node:Cstate_group, init:Tstate_group}|
		{ name: 'text', node:Ctext, init:Ttext}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber, parent:Cattributes) => new Cnumber(init, parent);
				case 'state group': return (init:Tstate_group, parent:Cattributes) => new Cstate_group(init, parent);
				case 'text': return (init:Ttext, parent:Cattributes) => new Ctext(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber {
	export class Dvalue extends AlanInteger {
		constructor(data:Tnumber['value'], parent:Cnumber) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group {
	export class Dnode extends Cconfiguration_node {
		constructor(data:Tstate_group['node'], parent:Cstate_group) {
			super(data, parent, {
				widget_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.selected_state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'node')
		}
	}
	export class Dselected_state extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group_type())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected state'`; }
	}
}
export namespace Ctext {
}
export namespace Cwidget_configuration_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno, init:Tno}|
		{ name: 'yes', node:Cyes, init:Tyes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno, parent:Cwidget_configuration_path) => new Cno(init, parent);
				case 'yes': return (init:Tyes, parent:Cwidget_configuration_path) => new Cyes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twidget_configuration_path['has steps'], parent:Cwidget_configuration_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes {
	export class Dattribute extends Reference<widget.Cattributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cyes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dtail extends Cwidget_configuration_path {
		constructor(data:Tyes['tail'], parent:Cyes) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'binding', node:Cbinding, init:Tbinding}|
		{ name: 'configuration', node:Cconfiguration, init:Tconfiguration}|
		{ name: 'window', node:Cwindow, init:Twindow}> {
		protected initializer(state: 'binding'|'configuration'|'window') {
			switch (state) {
				case 'binding': return (init:Tbinding, parent:Cyes) => new Cbinding(init, parent);
				case 'configuration': return (init:Tconfiguration, parent:Cyes) => new Cconfiguration(init, parent);
				case 'window': return (init:Twindow, parent:Cyes) => new Cwindow(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes['type'], parent:Cyes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconfiguration {
	export class Dtype extends StateGroup<
		{ name: 'list', node:Clist, init:Tlist}|
		{ name: 'state', node:Cstate, init:Tstate}> {
		protected initializer(state: 'list'|'state') {
			switch (state) {
				case 'list': return (init:Tlist, parent:Cconfiguration) => new Clist(init, parent);
				case 'state': return (init:Tstate, parent:Cconfiguration) => new Cstate(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconfiguration['type'], parent:Cconfiguration) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate {
	export class Dstate extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group_type())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cmodifications {
	export class Dconfiguration extends Cconfiguration_node {
		constructor(data:Tmodifications['configuration'], parent:Cmodifications) {
			super(data, parent, {
				widget_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.selected_state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'configuration')
		}
	}
	export class Dpath extends Cwidget_configuration_path {
		constructor(data:Tmodifications['path'], parent:Cmodifications) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.widget?.ref)
					.then(context => context?.properties.widget).result!)
			}, 'path')
		}
	}
	export class Dselected_state extends Reference<widget.Cstates__state_group,string> {

		constructor(data:string, $this:Cmodifications) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected state'`; }
	}
	export class Dstate_group extends Reference<widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node,string> {

		constructor(data:string, $this:Cmodifications) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'configuration') {
							return context.properties.type.state.node as widget.Cconfiguration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as widget.Cstate_group__type__configuration__type__attributes__widget_configuration_node;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
	export class Dwidget extends Reference<widget.Cwidget,string> {

		constructor(data:string, $this:Cmodifications) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.widget[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'widget'`; }
	}
}

export namespace Cmodifications {
	export function create(init:Tmodifications, input: {
	'widget':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile):Cmodifications {
		return new Cmodifications(init, input as any, symbols_file);
	};
}
